import type { HTMLAttributes, ReactNode } from "react";
import type { ModalOverlayProps as ReactAriaModalOverlayProps } from "react-aria-components";
import {
  Dialog as ReactAriaDialog,
  Heading as ReactAriaHeading,
  Modal as ReactAriaModal,
} from "react-aria-components";

import { useI18n } from "../use-i18n";
import { cn, cv, type VariantProps } from "../utils";
import { NcButton } from "./nc-button";
import { NcFormattedMessage } from "./nc-formatted-message";
import { NcIconCross } from "./nc-icons";
import { NcLoadingIndicator } from "./nc-loading-indicator";

const ncModalVariants = cv(
  [
    "border-ui bg-light text-dark relative m-4 max-h-[calc(100dvh-2rem)] rounded-md border shadow-xl outline-none sm:mx-auto",
    "data-entering:animate-slide-in-top data-exiting:animate-slide-out-top",
  ],
  {
    variants: {
      variant: {
        default: "w-full max-w-[40rem]",
        fitContent: "w-fit max-w-[calc(100dvw-2rem)]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export interface NcModalProps
  extends ReactAriaModalOverlayProps,
    VariantProps<typeof ncModalVariants> {
  children: ReactNode;
}

/** TODO: Work out how to use the ModalOverlay component to be able to style directly
 * Overlay is currently styled by className in the theme.css file.
 * When attempting this along with controlling the isOpen state the ability to
 * close the modal by clicking outside of it or with the escape key was lost
 */
const NcModal = ({
  children,
  isDismissable = true,
  onOpenChange,
  variant,
  className,
  ...props
}: NcModalProps) => {
  const { t } = useI18n();
  return (
    <>
      <ReactAriaModal
        data-nc="NcModal"
        className={cn(ncModalVariants({ variant }), className)}
        isDismissable={isDismissable}
        onOpenChange={onOpenChange}
        {...props}
      >
        <ReactAriaDialog className="flex max-h-[calc(100dvh-2rem)] flex-col outline-none!">
          <NcButton
            variant="icon"
            onPress={() => onOpenChange?.(false)}
            aria-label={t("close")}
            className="absolute top-1 right-1 z-10 p-2"
          >
            <NcIconCross />
          </NcButton>
          {children}
        </ReactAriaDialog>
      </ReactAriaModal>
    </>
  );
};

const NcModalHeader = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div data-nc="NcModal.Header" className={cn("border-ui border-b p-4", className)} {...props} />
);

const ModalHeading = ({
  children,
  className,
  ...props
}: {
  children: ReactNode;
  className?: string;
}) => (
  <ReactAriaHeading
    slot="title"
    className={cn("text-2xl leading-tight font-black", className)}
    {...props}
  >
    {children}
  </ReactAriaHeading>
);

interface NcModalBodyProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  error?: boolean | string;
  empty?: boolean | string;
  isLoading?: boolean;
}

type NcModalBodyState = "LOADING" | "LOADED" | "EMPTY" | "ERROR";

function getPanelState({
  isLoading,
  empty,
  error,
}: Omit<NcModalBodyProps, "children">): NcModalBodyState {
  if (isLoading) return "LOADING";
  if (empty) return "EMPTY";
  if (error) return "ERROR";
  return "LOADED";
}

const NcModalBody = ({
  children,
  isLoading,
  empty,
  error,
  className,
  ...props
}: NcModalBodyProps) => {
  const state = getPanelState({ isLoading, empty, error });
  if (state === "LOADED") {
    return (
      <div
        data-nc="NcModal.Body"
        className={cn("min-h-0 overflow-y-auto p-4", className)}
        {...props}
      >
        {children}
      </div>
    );
  }
  return (
    <div data-nc="NcModal.Body" className="min-h-0 overflow-y-auto p-4">
      {state === "LOADING" && (
        <div className="text-brand flex min-h-[20rem] w-full flex-grow items-center justify-center">
          <NcLoadingIndicator />
        </div>
      )}
      {state === "ERROR" && <NcPanelBodyError error={error} />}
      {state === "EMPTY" && <NcPanelBodyEmpty empty={empty} />}
    </div>
  );
};

const NcPanelBodyError = ({ error }: { error: NcModalBodyProps["error"] }) => {
  const { t } = useI18n();
  const errorText = typeof error === "string" ? error : t("error_default");
  return <NcFormattedMessage variant="danger">{errorText}</NcFormattedMessage>;
};

const NcPanelBodyEmpty = ({ empty }: { empty: NcModalBodyProps["empty"] }) => {
  const { t } = useI18n();
  const emptyText = typeof empty === "string" ? empty : t("empty_default");
  return <NcFormattedMessage variant="secondary">{emptyText}</NcFormattedMessage>;
};

const NcModalFooter = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div
    data-nc="NcModal.Footer"
    className={cn("border-ui flex items-center justify-end gap-2 border-t p-4", className)}
    {...props}
  />
);

NcModal.Header = NcModalHeader;
NcModal.Body = NcModalBody;
NcModal.Footer = NcModalFooter;
NcModal.Heading = ModalHeading;

export { NcModal };
